import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    ArrowRight,
    ArrowUpRight,
    Flame,
    Wind,
    Waves,
    Leaf,
    Bot,
    NotebookPen,
    Copy,
    Check,
    User,
    Calendar,
    Heart,
    BookOpen,
    Package,
    Stethoscope,
    Pencil,
    Pill,
    HeartPulse,
    ChevronRight,
    Activity,
    ClipboardList,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Money, Stars } from "@/components/ui-veda/Primitives";
import { DAILY_RITUALS, describeCombo } from "@/constants/dosha";
import PatientOnboarding from "./PatientOnboarding";
import { TID } from "@/constants/testIds";

const ELEMENT_ICON = { vata: Wind, pitta: Flame, kapha: Waves };

function Section({ icon: Icon, kicker, title, right, children, testId }) {
    return (
        <section
            data-testid={testId}
            className="border border-veda-border bg-veda-surface p-5 md:p-6"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                        <Icon size={14} />
                    </span>
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            {kicker}
                        </p>
                        <p className="font-display mt-0.5 text-lg text-veda-onSurface md:text-xl">
                            {title}
                        </p>
                    </div>
                </div>
                {right}
            </div>
            <div className="mt-4">{children}</div>
        </section>
    );
}

function ScorePicker({ label, value, onChange, testId }) {
    return (
        <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                {label}
            </p>
            <div className="mt-1.5 flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => onChange(n)}
                        data-testid={testId ? `${testId}-${n}` : undefined}
                        className={`h-8 flex-1 border text-xs font-semibold ${
                            value === n
                                ? "border-veda-brand bg-veda-brand text-white"
                                : "border-veda-border bg-veda-surface text-veda-onSurface"
                        }`}
                    >
                        {n}
                    </button>
                ))}
            </div>
        </div>
    );
}

function JournalForm({ onLogged }) {
    const [mood, setMood] = useState(3);
    const [sleep, setSleep] = useState(3);
    const [energy, setEnergy] = useState(3);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post("/journal", {
                mood,
                sleep,
                energy,
                note,
            });
            toast.success("Logged.");
            setNote("");
            onLogged?.(data);
        } catch {
            toast.error("Could not save.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <form
            onSubmit={submit}
            data-testid={TID.journalForm}
            className="grid gap-3 md:grid-cols-3"
        >
            <ScorePicker label="Mood" value={mood} onChange={setMood} testId={TID.journalMood} />
            <ScorePicker label="Sleep" value={sleep} onChange={setSleep} testId={TID.journalSleep} />
            <ScorePicker label="Energy" value={energy} onChange={setEnergy} testId={TID.journalEnergy} />
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="One line about your day (optional)"
                rows={2}
                data-testid={TID.journalNote}
                className="md:col-span-3 border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
            />
            <button
                type="submit"
                disabled={saving}
                data-testid={TID.journalSubmit}
                className="md:col-span-3 md:justify-self-end inline-flex items-center gap-2 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-70"
            >
                {saving ? "Logging…" : "Log today"}
            </button>
        </form>
    );
}

export default function PatientDashboard() {
    const { user } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/patient/dashboard");
            setDashboard(data);
        } catch {
            toast.error("Could not load your dashboard.");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Load lessons filtered by dosha (or all if not set)
    useEffect(() => {
        const primary = (dashboard?.dosha_profile?.primary || "").split("-")[0];
        api
            .get("/learn/lessons", primary ? { params: { dosha: primary } } : {})
            .then((r) => setLessons(r.data))
            .catch(() => {});
    }, [dashboard?.dosha_profile?.primary]);

    if (!dashboard) {
        return (
            <div className="veda-container py-20 text-center text-sm text-veda-muted">
                Loading your dashboard…
            </div>
        );
    }

    const profile = dashboard.profile;
    if (!profile || showEdit) {
        return (
            <div className="veda-container-narrow py-10 md:py-16">
                <PatientOnboarding
                    initial={profile}
                    onDone={() => {
                        setShowEdit(false);
                        load();
                    }}
                />
            </div>
        );
    }

    const dp = dashboard.dosha_profile;
    const info = dp ? describeCombo(dp.primary) : null;
    const primary = (dp?.primary || "").split("-")[0];
    const Icon = ELEMENT_ICON[primary] || Leaf;
    const rituals = DAILY_RITUALS[primary] || DAILY_RITUALS.vata;
    const completed = new Set(dashboard.today_completed_rituals || []);

    async function toggleRitual(key) {
        const nowDone = !completed.has(key);
        try {
            await api.post("/rituals/log", {
                ritual_key: key,
                completed: nowDone,
            });
            load();
        } catch {
            toast.error("Could not update ritual.");
        }
    }

    function copyCode() {
        navigator.clipboard.writeText(profile.patient_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }

    const consultations = dashboard.consultations || [];
    const recommended = dashboard.recommended_products || [];
    const journal = dashboard.journal || [];
    const health = dashboard.health_profile || null;
    const vitals = dashboard.latest_vitals || {};
    const pendingTests = dashboard.pending_tests_count || 0;

    const VITAL_ORDER = ["bp", "weight", "sugar_fasting", "pulse", "temperature", "spo2"];
    const VITAL_LABEL = {
        bp: "BP",
        weight: "Weight",
        sugar_fasting: "Sugar (F)",
        sugar_pp: "Sugar (PP)",
        pulse: "Pulse",
        temperature: "Temp",
        spo2: "SpO₂",
        waist: "Waist",
        custom: "Custom",
    };
    const orderedVitals = [
        ...VITAL_ORDER.filter((k) => vitals[k]),
        ...Object.keys(vitals).filter((k) => !VITAL_ORDER.includes(k)),
    ];

    const healthSummary = (() => {
        if (!health) return null;
        const bits = [];
        if (health.primary_complaint) bits.push(health.primary_complaint);
        if (health.sleep) bits.push(`sleep: ${health.sleep.replace(/_/g, " ")}`);
        if (health.digestion) bits.push(`agni: ${health.digestion.replace(/_/g, " ")}`);
        if (health.stress) bits.push(`stress: ${health.stress.replace(/_/g, " ")}`);
        return bits.slice(0, 4).join(" · ");
    })();

    return (
        <div data-testid={TID.patientDashboard} className="pb-16">
            {/* Header band */}
            <div className="border-b border-veda-border">
                <div className="veda-container py-8 md:py-12">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                My dashboard
                            </p>
                            <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                                Welcome back, {user?.name?.split(" ")[0] || "friend"}.
                            </h1>
                        </div>
                        <button
                            onClick={() => setShowEdit(true)}
                            data-testid={TID.dashboardEditProfile}
                            className="inline-flex items-center gap-1.5 border border-veda-border bg-veda-surface px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onSurface"
                        >
                            <Pencil size={11} /> Edit profile
                        </button>
                    </div>
                </div>
            </div>

            <div className="veda-container py-8 md:py-12">
                {/* Profile card + ID */}
                <Section
                    testId={TID.dashboardProfileCard}
                    icon={User}
                    kicker="Patient profile"
                    title={user?.name || "Patient"}
                >
                    <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <dt className="text-veda-muted">Email</dt>
                            <dd className="truncate text-veda-onSurface">
                                {user?.email}
                            </dd>
                            <dt className="text-veda-muted">Phone</dt>
                            <dd className="text-veda-onSurface">
                                {profile.phone_country} · {profile.phone}
                            </dd>
                            <dt className="text-veda-muted">DOB</dt>
                            <dd className="text-veda-onSurface">{profile.dob}</dd>
                            <dt className="text-veda-muted">Gender</dt>
                            <dd className="capitalize text-veda-onSurface">
                                {profile.gender.replace(/_/g, " ")}
                            </dd>
                        </dl>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <dt className="text-veda-muted">Height</dt>
                            <dd className="text-veda-onSurface">
                                {profile.height_cm} cm
                            </dd>
                            <dt className="text-veda-muted">Weight</dt>
                            <dd className="text-veda-onSurface">
                                {profile.weight_kg} kg
                            </dd>
                            <dt className="text-veda-muted">Blood group</dt>
                            <dd className="text-veda-onSurface">
                                {profile.blood_group}
                            </dd>
                            <dt className="text-veda-muted">City</dt>
                            <dd className="text-veda-onSurface">
                                {profile.location}
                            </dd>
                            <dt className="text-veda-muted">Occupation</dt>
                            <dd className="text-veda-onSurface">
                                {profile.occupation}
                            </dd>
                        </dl>
                        <div className="border border-veda-brand/40 bg-veda-brand/[0.06] p-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                                Patient ID
                            </p>
                            <p
                                data-testid={TID.dashboardPatientCode}
                                className="font-display mt-2 text-2xl tabular-nums text-veda-onSurface"
                            >
                                {profile.patient_code}
                            </p>
                            <button
                                onClick={copyCode}
                                data-testid={TID.dashboardCopyCode}
                                className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                            >
                                {copied ? <Check size={11} /> : <Copy size={11} />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                            <p className="mt-3 text-[10px] leading-relaxed text-veda-muted">
                                Share this with your Vaidya during
                                consultations for continuity of care.
                            </p>
                        </div>
                    </div>
                    {profile.illnesses?.length > 0 && (
                        <div className="mt-4 border-t border-veda-divider pt-4">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Health background
                            </p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {profile.illnesses.map((i) => (
                                    <span
                                        key={i}
                                        className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                    >
                                        {i}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </Section>

                {/* Ayurvedic Health Profile card */}
                <div className="mt-6">
                <Section
                    testId={TID.dashboardHealthCard}
                    icon={HeartPulse}
                    kicker="Ayurvedic health profile"
                    title={
                        health
                            ? "Your body & mind snapshot"
                            : "Help your Vaidya guide you better"
                    }
                    right={
                        <Link
                            to="/patient/health-profile"
                            data-testid={TID.dashboardHealthCta}
                            className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                        >
                            {health ? "Update" : "Fill now"} <ChevronRight size={11} />
                        </Link>
                    }
                >
                    {health ? (
                        <>
                            {healthSummary && (
                                <p className="text-sm capitalize leading-relaxed text-veda-onSurface">
                                    {healthSummary}
                                </p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-1.5">
                                {(health.allergies || []).slice(0, 4).map((a) => (
                                    <span
                                        key={`al-${a}`}
                                        className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                        title="Allergy"
                                    >
                                        · {a}
                                    </span>
                                ))}
                                {(health.medical_history || []).slice(0, 4).map((m) => (
                                    <span
                                        key={`mh-${m}`}
                                        className="rounded-pill border border-veda-brand/40 bg-veda-brand/[0.06] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-brand"
                                        title="Medical history"
                                    >
                                        {m}
                                    </span>
                                ))}
                                {(health.current_medicines || []).slice(0, 3).map((c) => (
                                    <span
                                        key={`cm-${c}`}
                                        className="rounded-pill border border-veda-border bg-veda-surface2 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-veda-onSurface"
                                        title="Current medicine"
                                    >
                                        ℞ {c}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-3 text-[10px] text-veda-muted">
                                Updated {new Date(health.updated_at).toLocaleDateString()} · Your Vaidya sees this before every consult, and the AI Guide uses it to personalise advice.
                            </p>
                        </>
                    ) : (
                        <p className="text-sm leading-relaxed text-veda-muted">
                            Take a couple of minutes to share how you sleep, eat,
                            move, and feel. Your Vaidya reads this before every
                            consult, and the AI Guide uses it to tailor every
                            suggestion to your body.
                        </p>
                    )}
                </Section>
                </div>

                {/* Vitals + Tests row */}
                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    <Section
                        testId={TID.dashboardVitalsCard}
                        icon={Activity}
                        kicker="Vitals timeline"
                        title={
                            orderedVitals.length
                                ? "Latest readings"
                                : "Start tracking BP, weight & more"
                        }
                        right={
                            <Link
                                to="/patient/vitals"
                                data-testid={TID.dashboardVitalsCta}
                                className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                            >
                                {orderedVitals.length ? "Log & view" : "Add first"}{" "}
                                <ChevronRight size={11} />
                            </Link>
                        }
                    >
                        {orderedVitals.length === 0 ? (
                            <p className="text-sm leading-relaxed text-veda-muted">
                                Log BP, weight, sugar and more once a week —
                                trends jump out during your consult.
                            </p>
                        ) : (
                            <ul className="grid gap-2 sm:grid-cols-2">
                                {orderedVitals.slice(0, 4).map((k) => {
                                    const v = vitals[k];
                                    const label =
                                        k === "custom"
                                            ? v.custom_name || "Custom"
                                            : VITAL_LABEL[k] || k;
                                    const display =
                                        k === "bp"
                                            ? `${Math.round(v.value)}/${Math.round(v.secondary)} ${v.unit || ""}`
                                            : `${v.value}${v.unit ? " " + v.unit : ""}`;
                                    return (
                                        <li
                                            key={k}
                                            className="border border-veda-border bg-veda-surface2 px-3 py-2"
                                        >
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                                {label}
                                            </p>
                                            <p className="font-display mt-0.5 text-lg text-veda-onSurface tabular-nums">
                                                {display}
                                            </p>
                                            <p className="text-[10px] text-veda-muted">
                                                {v.date}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Section>

                    <Section
                        testId={TID.dashboardTestsCard}
                        icon={ClipboardList}
                        kicker="Lab tests"
                        title={
                            pendingTests
                                ? `${pendingTests} pending`
                                : "Suggested tests & report uploads"
                        }
                        right={
                            <Link
                                to="/patient/tests"
                                data-testid={TID.dashboardTestsCta}
                                className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                            >
                                {pendingTests ? "Review" : "Open"}{" "}
                                <ChevronRight size={11} />
                            </Link>
                        }
                    >
                        {pendingTests > 0 ? (
                            <p className="text-sm leading-relaxed text-veda-onSurface">
                                Your Vaidya suggested {pendingTests} test
                                {pendingTests === 1 ? "" : "s"}. Upload the
                                report once you have it so the next consult
                                stays on-track.
                            </p>
                        ) : (
                            <p className="text-sm leading-relaxed text-veda-muted">
                                See tests suggested by your Vaidya, upload lab
                                reports (PDF/image), or request a test you'd
                                like reviewed.
                            </p>
                        )}
                    </Section>
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    {/* Dosha */}
                    <Section
                        testId={TID.dashboardDoshaCard}
                        icon={Icon}
                        kicker="Your prakriti"
                        title={dp ? info?.name : "Not taken yet"}
                        right={
                            <Link
                                to={dp ? "/wellness/profile" : "/wellness/quiz"}
                                className="text-xs font-medium text-veda-brand underline underline-offset-2"
                            >
                                {dp ? "View details" : "Take the quiz"}
                            </Link>
                        }
                    >
                        {dp ? (
                            <div className="flex flex-col gap-3">
                                <p className="text-sm leading-relaxed text-veda-muted">
                                    {info?.summary?.split(". ")[0]}.
                                </p>
                                <div className="grid grid-cols-3 gap-2">
                                    {["vata", "pitta", "kapha"].map((d) => (
                                        <div key={d} className="text-center">
                                            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                                {d}
                                            </p>
                                            <p className="font-display text-lg text-veda-onSurface">
                                                {dp.scores?.[d] ?? 0}%
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-veda-muted">
                                Take the 12-question prakriti quiz to unlock
                                personalised rituals and remedies across your
                                dashboard.
                            </p>
                        )}
                    </Section>

                    {/* Streak & rituals */}
                    <Section
                        testId={TID.dashboardRituals}
                        icon={Flame}
                        kicker={`Streak · ${dashboard.ritual_streak || 0} day${dashboard.ritual_streak === 1 ? "" : "s"}`}
                        title="Today's rituals"
                    >
                        <ul className="flex flex-col gap-2">
                            {rituals.map((r) => {
                                const key = r.title.toLowerCase().replace(/\s+/g, "-").slice(0, 40);
                                const done = completed.has(key);
                                return (
                                    <li key={key}>
                                        <button
                                            onClick={() => toggleRitual(key)}
                                            data-testid={TID.dashboardRitualToggle(key)}
                                            className={`flex w-full items-start gap-3 border p-3 text-left ${
                                                done
                                                    ? "border-veda-brand bg-veda-brand/10"
                                                    : "border-veda-border bg-veda-surface hover:border-veda-borderStrong"
                                            }`}
                                        >
                                            <span
                                                className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center border ${
                                                    done
                                                        ? "border-veda-brand bg-veda-brand text-white"
                                                        : "border-veda-border"
                                                }`}
                                            >
                                                {done && <Check size={11} />}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand">
                                                    {r.time}
                                                </p>
                                                <p className="text-sm font-semibold text-veda-onSurface">
                                                    {r.title}
                                                </p>
                                                <p className="mt-0.5 text-xs text-veda-muted">
                                                    {r.body}
                                                </p>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </Section>

                    {/* Consultations */}
                    <Section
                        testId={TID.dashboardConsultations}
                        icon={Stethoscope}
                        kicker="Consultations"
                        title={
                            consultations.length
                                ? `${consultations.length} with a Vaidya`
                                : "No consultations yet"
                        }
                        right={
                            <Link
                                to="/shop"
                                className="text-xs font-medium text-veda-brand underline underline-offset-2"
                            >
                                Find a Vaidya
                            </Link>
                        }
                    >
                        {consultations.length === 0 ? (
                            <p className="text-sm text-veda-muted">
                                Once you book, past visits, prescriptions and
                                notes from your Vaidya appear here.
                            </p>
                        ) : (
                            <ul className="divide-y divide-veda-divider">
                                {consultations.slice(0, 5).map((c) => (
                                    <li
                                        key={c.consultation_id}
                                        className="flex flex-col gap-2 py-3"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="grid h-9 w-9 shrink-0 place-items-center bg-veda-brand/10 text-veda-brand">
                                                <Calendar size={14} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-veda-onSurface">
                                                    {c.doctor_name}
                                                </p>
                                                <p className="truncate text-xs text-veda-muted">
                                                    {c.slot} · {c.mode} ·{" "}
                                                    <span className="font-semibold text-veda-onSurface capitalize">
                                                        {c.status || "confirmed"}
                                                    </span>
                                                </p>
                                                {c.notes && (
                                                    <p className="mt-1 line-clamp-2 text-xs italic text-veda-muted">
                                                        Note: {c.notes}
                                                    </p>
                                                )}
                                            </div>
                                            <Link
                                                to={`/consultation/${c.consultation_id}`}
                                                className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-brand"
                                            >
                                                Open
                                            </Link>
                                        </div>
                                        {(c.prescriptions || []).length > 0 && (
                                            <div
                                                data-testid={TID.patientRxList(c.consultation_id)}
                                                className="ml-12 border-l-2 border-veda-brand pl-3"
                                            >
                                                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-brand">
                                                    <Pill size={11} /> Prescriptions
                                                </p>
                                                <ul className="mt-1.5 flex flex-col gap-1">
                                                    {c.prescriptions.map((r) => (
                                                        <li
                                                            key={r.prescription_id}
                                                            className="text-[11px] text-veda-onSurface"
                                                        >
                                                            <span className="font-semibold">
                                                                {r.medicine}
                                                            </span>
                                                            {" · "}
                                                            {r.dosage}
                                                            {" · "}
                                                            {r.frequency}
                                                            {" · "}
                                                            <span className="font-semibold">
                                                                {r.days} days
                                                            </span>
                                                            {r.notes && (
                                                                <span className="mt-0.5 block italic text-veda-muted">
                                                                    {r.notes}
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Section>

                    {/* Wellness journal */}
                    <Section
                        testId={TID.dashboardJournal}
                        icon={NotebookPen}
                        kicker="Wellness journal"
                        title="How are you today?"
                    >
                        <JournalForm onLogged={load} />
                        {journal.length > 0 && (
                            <div className="mt-6 border-t border-veda-divider pt-4">
                                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                    Recent entries
                                </p>
                                <ul className="mt-2 grid grid-cols-4 gap-1 sm:grid-cols-7">
                                    {journal.slice(0, 7).map((j) => (
                                        <li
                                            key={j.entry_id}
                                            title={`Mood ${j.mood}/5 · Sleep ${j.sleep}/5 · Energy ${j.energy}/5`}
                                            className="border border-veda-border bg-veda-surface2 p-2 text-center"
                                        >
                                            <p className="text-[9px] font-semibold uppercase text-veda-muted">
                                                {j.date.slice(5)}
                                            </p>
                                            <p className="mt-1 text-[10px] tabular-nums text-veda-onSurface">
                                                {j.mood}·{j.sleep}·{j.energy}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-[9px] text-veda-muted">
                                    Format: mood · sleep · energy (1–5)
                                </p>
                            </div>
                        )}
                    </Section>
                </div>

                {/* Learn library */}
                <Section
                    testId={TID.dashboardLearn}
                    icon={BookOpen}
                    kicker="Learn"
                    title={
                        primary
                            ? `Ayurveda for ${primary}`
                            : "Ayurveda in 3-minute lessons"
                    }
                >
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {lessons.slice(0, 6).map((l) => (
                            <details
                                key={l.lesson_id}
                                data-testid={TID.learnLessonCard(l.lesson_id)}
                                className="group border border-veda-border bg-veda-surface p-4"
                            >
                                <summary className="cursor-pointer list-none">
                                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-brand">
                                        {l.read_minutes} min read
                                    </p>
                                    <p className="font-display mt-1 text-base text-veda-onSurface">
                                        {l.title}
                                    </p>
                                    <p className="mt-1 text-xs text-veda-muted">
                                        {l.summary}
                                    </p>
                                </summary>
                                <p className="mt-3 border-t border-veda-divider pt-3 text-sm leading-relaxed text-veda-onSurface/85">
                                    {l.body}
                                </p>
                            </details>
                        ))}
                    </div>
                </Section>

                {/* Recommended products */}
                {recommended.length > 0 && (
                    <Section
                        testId={TID.dashboardRecommendations}
                        icon={Package}
                        kicker="Recommended for you"
                        title={
                            profile.illnesses?.length
                                ? "Matched to your health background"
                                : "Popular remedies"
                        }
                        right={
                            <Link
                                to="/shop"
                                className="text-xs font-medium text-veda-brand underline underline-offset-2"
                            >
                                Full shop
                            </Link>
                        }
                    >
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                            {recommended.map((p) => (
                                <Link
                                    key={p.product_id}
                                    to={`/product/${p.product_id}`}
                                    data-testid={TID.recommendedCard(p.product_id)}
                                    className="group block border border-veda-border bg-veda-surface"
                                >
                                    <div className="aspect-[4/5] w-full overflow-hidden bg-veda-surface2">
                                        <img
                                            src={p.image_url}
                                            alt={p.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-2">
                                        <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                            {p.category}
                                        </p>
                                        <p className="font-display line-clamp-2 text-xs text-veda-onSurface md:text-sm">
                                            {p.name}
                                        </p>
                                        <div className="mt-1 flex items-center justify-between">
                                            <p className="text-xs font-semibold text-veda-onSurface">
                                                <Money value={p.price} />
                                            </p>
                                            {p.rating > 0 && (
                                                <Stars value={p.rating} size={9} />
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </Section>
                )}

                {/* AI Guide CTA */}
                <div className="mt-6 border border-veda-brand/40 bg-veda-brand/[0.06] p-5 md:p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <span className="grid h-9 w-9 place-items-center bg-veda-brand/15 text-veda-brand">
                                <Bot size={16} />
                            </span>
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                                    AI Guide
                                </p>
                                <p className="font-display mt-0.5 text-lg text-veda-onSurface">
                                    Ask about {profile.illnesses?.[0] || "anything today"}
                                </p>
                            </div>
                        </div>
                        <Link
                            to="/wellness/guide"
                            className="inline-flex items-center gap-1.5 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                        >
                            Open the Guide <ArrowUpRight size={12} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
